import React, { useCallback, useEffect, useState } from "react";
import { format, startOfMonth, endOfMonth, lastDayOfMonth } from "date-fns";
import { InformationCircleIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  getPayoutRuns,
  createPayoutRun,
  updatePayoutRun,
  getPayoutLines,
  getPayoutPayments,
  recordPayoutPayments,
  updatePayoutLine,
  type PayoutRun,
  type PayoutRunStatus,
  type PayoutLine,
  type PayoutPayment,
  type RecordPayoutPaymentsPayload,
} from "../../apis/payoutRun";
import { getClinicUsers } from "../../apis/user";
import { useNotification } from "../../NotificationContext";

type TabType = "run_payroll" | "payroll_history";

const STATUS_LABEL: Record<PayoutRunStatus, string> = {
  draft: "Draft",
  finalized: "Finalized",
  paid: "Paid",
  cancelled: "Cancelled",
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  hold: "Hold",
  paid: "Paid",
  yet_to_pay: "Yet to pay",
  failed: "Failed",
  cancelled: "Cancelled",
};

const PAYMENT_MODE_OPTIONS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "cash", label: "Cash" },
] as const;

type ClinicUser = { id: number; firstName?: string; lastName?: string };

/** Group lines by payee and attach payment status */
function groupLinesByPayee(
  lines: PayoutLine[],
  payments: PayoutPayment[],
  clinicUsers: ClinicUser[]
): { payeeId: number; payeeName: string; totalAmount: number; status: string; lines: PayoutLine[] }[] {
  const byPayee = new Map<number, PayoutLine[]>();
  for (const line of lines) {
    const id = line.payeeId;
    if (!byPayee.has(id)) byPayee.set(id, []);
    byPayee.get(id)!.push(line);
  }
  const paymentByPayee = new Map<number, PayoutPayment>();
  for (const p of payments) {
    paymentByPayee.set(p.payeeId, p);
  }
  const getUserName = (id: number) => {
    const u = clinicUsers.find((c) => c.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(" ") || `User #${id}` : `User #${id}`;
  };
  return Array.from(byPayee.entries())
    .map(([payeeId, payeeLines]) => {
      const totalAmount = payeeLines.reduce((sum, l) => sum + (l.payoutAmount ?? 0), 0);
      const payment = paymentByPayee.get(payeeId);
      const status = payment?.status === "paid" ? "paid" : "yet_to_pay";
      return {
        payeeId,
        payeeName: getUserName(payeeId),
        totalAmount,
        status,
        lines: payeeLines,
      };
    })
    .sort((a, b) => a.payeeName.localeCompare(b.payeeName));
}

const DoctorPayouts: React.FC = () => {
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState<TabType>("run_payroll");
  const [runs, setRuns] = useState<PayoutRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detailRunId, setDetailRunId] = useState<number | null>(null);
  const [detailLines, setDetailLines] = useState<PayoutLine[]>([]);
  const [detailPayments, setDetailPayments] = useState<PayoutPayment[]>([]);
  const [clinicUsers, setClinicUsers] = useState<ClinicUser[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedPayees, setExpandedPayees] = useState<Set<number>>(new Set());
  const [selectedPayeeIds, setSelectedPayeeIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [recordModalPaymentDate, setRecordModalPaymentDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [recordModalSendNotification, setRecordModalSendNotification] = useState(false);
  const [recordModalPaymentMode, setRecordModalPaymentMode] = useState<string>("bank_transfer");
  const [recordModalSelectedPayeeIds, setRecordModalSelectedPayeeIds] = useState<Set<number>>(new Set());
  const [updatingLineId, setUpdatingLineId] = useState<number | null>(null);
  const [editingLineAmount, setEditingLineAmount] = useState<Record<number, string>>({});

  const now = new Date();
  const periodStart = format(startOfMonth(now), "yyyy-MM-dd");
  const periodEnd = format(endOfMonth(now), "yyyy-MM-dd");
  const currentMonthLabel = format(now, "MMMM yyyy");
  const lastDay = lastDayOfMonth(now);
  const payDay = Math.min(30, lastDay.getDate());
  const payDate = format(new Date(now.getFullYear(), now.getMonth(), payDay), "dd/MM/yyyy");
  const isPastPayDay = now.getDate() > payDay;

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getPayoutRuns({
        payoutType: "doctor",
        perPage: 100,
      });
      const data = res?.data?.data ?? res?.data ?? res;
      setRuns(Array.isArray(data) ? data : []);
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to load pay runs",
        err?.response?.data?.message ?? "",
        "topRight"
      );
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    getClinicUsers().then((res: any) => {
      const data = res?.data ?? res;
      setClinicUsers(Array.isArray(data) ? data : []);
    }).catch(() => setClinicUsers([]));
  }, []);

  const openDraftDetails = useCallback(async (runId: number) => {
    setDetailRunId(runId);
    setDetailLoading(true);
    setDetailLines([]);
    setDetailPayments([]);
    setExpandedPayees(new Set());
    try {
      const [linesRes, paymentsRes] = await Promise.all([
        getPayoutLines(runId),
        getPayoutPayments(runId),
      ]);
      const linesData = (linesRes as any)?.data?.data ?? (linesRes as any)?.data ?? [];
      const paymentsData = (paymentsRes as any)?.data?.data ?? (paymentsRes as any)?.data ?? [];
      setDetailLines(Array.isArray(linesData) ? linesData : []);
      setDetailPayments(Array.isArray(paymentsData) ? paymentsData : []);
    } catch (err: any) {
      showNotification("error", "Failed to load payout details", err?.response?.data?.message ?? "", "topRight");
    } finally {
      setDetailLoading(false);
    }
  }, [showNotification]);

  const closeDraftDetails = useCallback(() => {
    setDetailRunId(null);
    setDetailLines([]);
    setDetailPayments([]);
    setExpandedPayees(new Set());
    setSelectedPayeeIds(new Set());
  }, []);

  const groupedPayeesForModal = groupLinesByPayee(detailLines, detailPayments, clinicUsers);

  const openRecordModal = useCallback(
    (forAll: boolean) => {
      setRecordModalPaymentDate(format(new Date(), "yyyy-MM-dd"));
      setRecordModalSendNotification(false);
      setRecordModalPaymentMode("bank_transfer");
      if (forAll) {
        setRecordModalSelectedPayeeIds(new Set(groupedPayeesForModal.map((r) => r.payeeId)));
      } else {
        setRecordModalSelectedPayeeIds(new Set(selectedPayeeIds));
      }
      setRecordModalOpen(true);
    },
    [groupedPayeesForModal, selectedPayeeIds]
  );

  const closeRecordModal = useCallback(() => {
    setRecordModalOpen(false);
  }, []);

  const toggleRecordModalPayee = useCallback((payeeId: number) => {
    setRecordModalSelectedPayeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(payeeId)) next.delete(payeeId);
      else next.add(payeeId);
      return next;
    });
  }, []);

  const handleRecordPaymentSubmit = useCallback(async () => {
    if (!detailRunId) return;
    const payeeIds = Array.from(recordModalSelectedPayeeIds);
    if (payeeIds.length === 0) {
      showNotification("info", "Select at least one payee", "", "topRight");
      return;
    }
    setRecording(true);
    try {
      const payload: RecordPayoutPaymentsPayload = {
        payoutRunId: detailRunId,
        payeeIds,
        paidAt: recordModalPaymentDate,
        mode: recordModalPaymentMode,
        sendNotification: recordModalSendNotification,
      };
      await recordPayoutPayments(payload);
      showNotification("success", "Payment(s) recorded", "", "topRight");
      const [_, paymentsRes] = await Promise.all([
        getPayoutLines(detailRunId),
        getPayoutPayments(detailRunId),
      ]);
      const paymentsData = (paymentsRes as any)?.data?.data ?? (paymentsRes as any)?.data ?? [];
      setDetailPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setSelectedPayeeIds(new Set());
      closeRecordModal();
      fetchRuns();
    } catch (err: any) {
      showNotification("error", "Failed to record payment", err?.response?.data?.message ?? "", "topRight");
    } finally {
      setRecording(false);
    }
  }, [
    detailRunId,
    recordModalSelectedPayeeIds,
    recordModalPaymentDate,
    recordModalPaymentMode,
    recordModalSendNotification,
    showNotification,
    fetchRuns,
    closeRecordModal,
  ]);

  const togglePayeeSelection = useCallback((payeeId: number) => {
    setSelectedPayeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(payeeId)) next.delete(payeeId);
      else next.add(payeeId);
      return next;
    });
  }, []);

  const selectAllPayees = useCallback((payeeIds: number[]) => {
    setSelectedPayeeIds(new Set(payeeIds));
  }, []);

  const clearPayeeSelection = useCallback(() => {
    setSelectedPayeeIds(new Set());
  }, []);

  const handleUpdateLineAmount = useCallback(
    async (lineId: number, amount: number) => {
      if (!detailRunId) return;
      setUpdatingLineId(lineId);
      try {
        await updatePayoutLine(lineId, { payoutAmount: amount });
        const res = await getPayoutLines(detailRunId);
        const data = (res as any)?.data?.data ?? (res as any)?.data ?? [];
        setDetailLines(Array.isArray(data) ? data : []);
        showNotification("success", "Payout amount updated", "", "topRight");
      } catch (err: any) {
        showNotification("error", "Failed to update amount", err?.response?.data?.message ?? "", "topRight");
      } finally {
        setUpdatingLineId(null);
      }
    },
    [detailRunId, showNotification]
  );

  const togglePayeeExpanded = useCallback((payeeId: number) => {
    setExpandedPayees((prev) => {
      const next = new Set(prev);
      if (next.has(payeeId)) next.delete(payeeId);
      else next.add(payeeId);
      return next;
    });
  }, []);

  const currentRun = runs.find(
    (r) => r.periodStart <= periodEnd && r.periodEnd >= periodStart && r.status !== "cancelled"
  );
  const summary = currentRun?.summary ?? {
  };
  const doctorCount = clinicUsers.length;
  const netPay = summary.netPay;
  const netPayDisplay =
    netPay != null && netPay !== "" ? `₹${Number(netPay).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";
  const periodLabel =
    currentRun?.periodStart && currentRun?.periodEnd
      ? `${format(new Date(currentRun.periodStart), "d MMM yyyy")} – ${format(new Date(currentRun.periodEnd), "d MMM yyyy")}`
      : `${format(new Date(periodStart), "d MMM yyyy")} – ${format(new Date(periodEnd), "d MMM yyyy")}`;
  const paymentDateDisplay = currentRun?.paidAt
    ? format(new Date(currentRun.paidAt), "dd/MM/yyyy")
    : payDate;
  const status: PayoutRunStatus = currentRun?.status ?? "draft";
  const statusLabel = currentRun ? STATUS_LABEL[status] : "READY";

  const handleSubmitAndApproveRun = useCallback(async () => {
    if (!currentRun || currentRun.status !== "draft") return;
    setSubmitting(true);
    try {
      await updatePayoutRun(currentRun.id, { status: "finalized" });
      showNotification("success", "Pay run submitted and approved", "", "topRight");
      fetchRuns();
      closeDraftDetails();
    } catch (err: any) {
      showNotification("error", "Failed to approve", err?.response?.data?.message ?? "", "topRight");
    } finally {
      setSubmitting(false);
    }
  }, [currentRun, showNotification, fetchRuns, closeDraftDetails]);

  const handleCreatePayRun = async () => {
    if (currentRun) {
      showNotification("info", "A pay run already exists for this period", "", "topRight");
      return;
    }
    setCreating(true);
    try {
      await createPayoutRun({
        payoutType: "doctor",
        periodStart,
        periodEnd,
        status: "draft",
      });
      showNotification("success", "Pay run created", "", "topRight");
      fetchRuns();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to create pay run",
        err?.response?.data?.message ?? "",
        "topRight"
      );
    } finally {
      setCreating(false);
    }
  };

  const historyRuns = runs
    .filter((r) => r.payoutType === "doctor")
    .sort((a, b) => (b.periodStart || "").localeCompare(a.periodStart || ""));

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex-auto">
        <h1 className="text-base font-semibold leading-6 text-gray-900">
          Doctor Payouts
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Generate pay runs and manage doctor payout history.
        </p>
      </div>

      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab("run_payroll")}
            className={`whitespace-nowrap border-b-2 py-4 text-sm font-medium ${
              activeTab === "run_payroll"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            Run Payroll
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("payroll_history")}
            className={`whitespace-nowrap border-b-2 py-4 text-sm font-medium ${
              activeTab === "payroll_history"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
          >
            Payroll History
          </button>
        </nav>
      </div>

      {activeTab === "run_payroll" && (
        <div className="mt-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-6">
              {loading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                <>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-base font-semibold text-gray-900">
                        Process Pay Run for {currentMonthLabel}
                      </h2>
                      <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-white">
                        {statusLabel}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {currentRun && (status === "draft" || status === "finalized") && (
                        <button
                          type="button"
                          onClick={() => openDraftDetails(currentRun.id)}
                          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        >
                          View details
                        </button>
                      )}
                      {!currentRun && (
                        <button
                          type="button"
                          onClick={handleCreatePayRun}
                          disabled={creating}
                          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                        >
                          {creating ? "Creating..." : "Create Pay Run"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-8">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Period
                      </p>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {periodLabel}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        No. of Doctors
                      </p>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {doctorCount}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Doctors&apos; Net Pay
                      </p>
                      <p className="mt-1 text-base font-semibold text-gray-900">
                        {netPayDisplay}
                      </p>
                    </div>
                  </div>

                  {isPastPayDay && !currentRun && (
                    <div className="mt-6 flex items-start gap-2 rounded-md bg-gray-50 px-3 py-2">
                      <InformationCircleIcon className="h-5 w-5 shrink-0 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        You haven&apos;t processed this pay run and it&apos;s past the pay day.
                      </p>
                    </div>
                  )}

                  {detailRunId !== null && detailRunId === currentRun?.id && (
                    <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">Payout details (grouped by payee)</h3>
                        <div className="flex flex-wrap items-center gap-2">
                          {currentRun?.status === "draft" && (
                            <button
                              type="button"
                              onClick={handleSubmitAndApproveRun}
                              disabled={submitting}
                              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-hover disabled:opacity-50"
                            >
                              {submitting ? "Submitting..." : "Submit and Approve"}
                            </button>
                          )}
                          {currentRun?.status === "finalized" && (
                            <>
                              <button
                                type="button"
                                onClick={() => openRecordModal(true)}
                                disabled={recording}
                                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-hover disabled:opacity-50"
                              >
                                Record for entire run
                              </button>
                              <button
                                type="button"
                                onClick={() => openRecordModal(false)}
                                disabled={recording || selectedPayeeIds.size === 0}
                                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Record payment ({selectedPayeeIds.size} selected)
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={closeDraftDetails}
                            className="text-sm font-medium text-gray-600 hover:text-gray-900"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                      {detailLoading ? (
                        <p className="mt-4 text-sm text-gray-500">Loading lines and payments...</p>
                      ) : (
                        <div className="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                          <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                              <tr>
                                {currentRun?.status === "finalized" && (
                                  <th className="w-10 py-3 pl-4 pr-2 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                    <input
                                      type="checkbox"
                                      checked={(() => {
                                        const grouped = groupLinesByPayee(detailLines, detailPayments, clinicUsers);
                                        return grouped.length > 0 && grouped.every((r) => selectedPayeeIds.has(r.payeeId));
                                      })()}
                                      onChange={(e) => {
                                        const grouped = groupLinesByPayee(detailLines, detailPayments, clinicUsers);
                                        if (e.target.checked) selectAllPayees(grouped.map((r) => r.payeeId));
                                        else clearPayeeSelection();
                                      }}
                                      className="h-4 w-4 rounded border-gray-300"
                                    />
                                  </th>
                                )}
                                <th className="w-8 py-3 pl-4 pr-2 text-left text-sm font-semibold text-gray-900 sm:pl-6" />
                                <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold text-gray-900">
                                  Payee
                                </th>
                                <th className="px-3 py-3 text-right text-sm font-semibold text-gray-900">
                                  Amount
                                </th>
                                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(() => {
                                const grouped = groupLinesByPayee(detailLines, detailPayments, clinicUsers);
                                if (grouped.length === 0) {
                                  return (
                                    <tr>
                                      <td colSpan={currentRun?.status === "finalized" ? 5 : 4} className="py-8 text-center text-sm text-gray-500">
                                        No payout lines yet. Add visits/services to generate lines.
                                      </td>
                                    </tr>
                                  );
                                }
                                return grouped.map((row) => {
                                  const isExpanded = expandedPayees.has(row.payeeId);
                                  return (
                                    <React.Fragment key={row.payeeId}>
                                      <tr className="bg-white hover:bg-gray-50">
                                        {currentRun?.status === "finalized" && (
                                          <td className="w-10 py-2 pl-4 pr-2 sm:pl-6">
                                            <input
                                              type="checkbox"
                                              checked={selectedPayeeIds.has(row.payeeId)}
                                              onChange={() => togglePayeeSelection(row.payeeId)}
                                              className="h-4 w-4 rounded border-gray-300"
                                            />
                                          </td>
                                        )}
                                        <td className="w-8 py-2 pl-4 pr-2 sm:pl-6">
                                          {row.lines.length > 0 ? (
                                            <button
                                              type="button"
                                              onClick={() => togglePayeeExpanded(row.payeeId)}
                                              className="text-gray-500 hover:text-gray-700"
                                            >
                                              {isExpanded ? (
                                                <ChevronDownIcon className="h-4 w-4" />
                                              ) : (
                                                <ChevronRightIcon className="h-4 w-4" />
                                              )}
                                            </button>
                                          ) : null}
                                        </td>
                                        <td className="py-2 pl-2 pr-3 text-sm font-medium text-gray-900">
                                          {row.payeeName}
                                        </td>
                                        <td className="px-3 py-2 text-right text-sm text-gray-900">
                                          ₹{row.totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-3 py-2">
                                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${row.status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                                            {PAYMENT_STATUS_LABEL[row.status] ?? row.status}
                                          </span>
                                        </td>
                                      </tr>
                                      {isExpanded &&
                                        row.lines.map((line) => {
                                          const isDraft = currentRun?.status === "draft";
                                          const displayAmount = editingLineAmount[line.id] ?? String(line.payoutAmount ?? 0);
                                          return (
                                            <tr key={line.id} className="bg-gray-50/80">
                                              {currentRun?.status === "finalized" && <td className="w-10 py-1.5 pl-4 pr-2 sm:pl-6" />}
                                              <td className="w-8 py-1.5 pl-4 pr-2 sm:pl-6" />
                                              <td className="py-1.5 pl-8 pr-3 text-xs text-gray-600">
                                                Line #{line.id}
                                                {line.serviceDate != null && ` · ${format(new Date(line.serviceDate), "dd MMM yyyy")}`}
                                                {line.sourceType != null && ` · ${line.sourceType}`}
                                              </td>
                                              <td className="px-3 py-1.5 text-right text-xs text-gray-600">
                                                {isDraft ? (
                                                  <span className="inline-flex items-center gap-1">
                                                    <span>₹</span>
                                                    <input
                                                      type="number"
                                                      min={0}
                                                      step={0.01}
                                                      value={displayAmount}
                                                      onChange={(e) => setEditingLineAmount((prev) => ({ ...prev, [line.id]: e.target.value }))}
                                                      onBlur={() => {
                                                        const v = parseFloat(displayAmount);
                                                        if (!Number.isNaN(v) && v >= 0 && v !== (line.payoutAmount ?? 0)) {
                                                          handleUpdateLineAmount(line.id, v);
                                                        }
                                                        setEditingLineAmount((prev) => {
                                                          const next = { ...prev };
                                                          delete next[line.id];
                                                          return next;
                                                        });
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                          (e.target as HTMLInputElement).blur();
                                                        }
                                                      }}
                                                      disabled={updatingLineId === line.id}
                                                      className="w-24 text-right rounded border border-gray-300 px-1.5 py-0.5 text-xs"
                                                    />
                                                    {updatingLineId === line.id && (
                                                      <span className="text-gray-400 text-xs">Saving...</span>
                                                    )}
                                                  </span>
                                                ) : (
                                                  `₹${(line.payoutAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                                                )}
                                              </td>
                                              <td className="px-3 py-1.5" />
                                            </tr>
                                          );
                                        })}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "payroll_history" && (
        <div className="mt-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="p-6">
              {loading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Period
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Payment Date
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          No. of Doctors
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Net Pay
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {historyRuns.length > 0 ? (
                        historyRuns.map((run) => {
                          const s = run.summary ?? {};
                          const count = s.doctorCount ?? 0;
                          const net = s.netPay;
                          const netStr =
                            net != null && net !== ""
                              ? `₹${Number(net).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                              : "—";
                          const periodLabel =
                            run.periodStart && run.periodEnd
                              ? `${format(new Date(run.periodStart), "MMM d, yyyy")} – ${format(new Date(run.periodEnd), "MMM d, yyyy")}`
                              : "—";
                          const paidAt = run.paidAt;
                          const paymentDate = paidAt
                            ? format(new Date(paidAt), "dd/MM/yyyy")
                            : payDate;
                          return (
                            <tr key={run.id}>
                              <td className="py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                                {periodLabel}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {paymentDate}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {count}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-500">
                                {netStr}
                              </td>
                              <td className="px-3 py-4">
                                <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-white">
                                  {STATUS_LABEL[run.status as PayoutRunStatus] ?? run.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="py-8 text-center text-sm text-gray-500"
                          >
                            No payroll history yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {recordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden mx-4">
            <div className="px-6 py-4 border-b font-semibold text-lg flex justify-between items-center">
              <span>Record payment</span>
              <button
                type="button"
                onClick={closeRecordModal}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold leading-none"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment date</label>
                <input
                  type="date"
                  value={recordModalPaymentDate}
                  onChange={(e) => setRecordModalPaymentDate(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment mode</label>
                <select
                  value={recordModalPaymentMode}
                  onChange={(e) => setRecordModalPaymentMode(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                >
                  {PAYMENT_MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="record-modal-notify"
                  checked={recordModalSendNotification}
                  onChange={(e) => setRecordModalSendNotification(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="record-modal-notify" className="text-sm text-gray-700">
                  Send notification to payees
                </label>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Select payees</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="w-10 py-2 pl-3 pr-2 text-left">
                          <input
                            type="checkbox"
                            checked={groupedPayeesForModal.length > 0 && groupedPayeesForModal.every((r) => recordModalSelectedPayeeIds.has(r.payeeId))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRecordModalSelectedPayeeIds(new Set(groupedPayeesForModal.map((r) => r.payeeId)));
                              } else {
                                setRecordModalSelectedPayeeIds(new Set());
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </th>
                        <th className="py-2 px-2 text-left text-xs font-semibold text-gray-600">Payee</th>
                        <th className="py-2 px-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                        <th className="py-2 px-2 text-left text-xs font-semibold text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupedPayeesForModal.map((row) => (
                        <tr key={row.payeeId} className="hover:bg-gray-50">
                          <td className="w-10 py-2 pl-3 pr-2">
                            <input
                              type="checkbox"
                              checked={recordModalSelectedPayeeIds.has(row.payeeId)}
                              onChange={() => toggleRecordModalPayee(row.payeeId)}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </td>
                          <td className="py-2 px-2 text-sm text-gray-900">{row.payeeName}</td>
                          <td className="py-2 px-2 text-sm text-right text-gray-900">
                            ₹{row.totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-2 px-2">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${row.status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                              {PAYMENT_STATUS_LABEL[row.status] ?? row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {groupedPayeesForModal.length === 0 && (
                  <p className="text-sm text-gray-500 py-2">No payees in this run.</p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={closeRecordModal}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRecordPaymentSubmit}
                disabled={recording || recordModalSelectedPayeeIds.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {recording ? "Recording..." : "Record payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorPayouts;
